import { getSetting, setSetting } from './db';
import {
  ALL_PACKS,
  loadPackIndex,
  loadRegions,
  resolveSelection,
  type PackSummary,
} from './region';

// Which packs the learner is studying, and the one place that changes.
//
// Held in the synced settings map rather than a store of its own: it is a
// preference that should follow an account across devices, and that map is
// schemaless JSON in both backends, so adding a key needs no migration and no
// SCHEMA_VERSION bump. Cards are keyed `code:skill` with no pack in them, so
// nothing already written changes meaning when the selection does.
export const PACKS_SETTING = 'packs.active';

/** A selection is one pack's id, or ALL_PACKS. */
export type PackSelection = string;

export async function storedSelection(): Promise<PackSelection | undefined> {
  return getSetting<string>(PACKS_SETTING);
}

/**
 * Load whatever the stored selection resolves to.
 *
 * Never throws over a selection it can't honour — `resolveSelection` falls back
 * to the default pack for a name this build no longer ships. The setting is
 * synced and outlives any one release, so a withdrawn pack should cost the
 * learner their choice, not their app.
 */
export async function loadSelectedPacks(): Promise<PackSummary[]> {
  const available = await loadPackIndex();
  await loadRegions(resolveSelection(await storedSelection(), available));
  return available;
}

/** Record a new selection and install it. */
export async function selectPacks(selection: PackSelection): Promise<void> {
  const available = await loadPackIndex();
  await loadRegions(resolveSelection(selection, available));
  await setSetting(PACKS_SETTING, selection);
}

/**
 * What the picker should show as chosen.
 *
 * Read back through `resolveSelection` rather than returned raw, so a stored id
 * the build no longer ships shows the pack actually in force instead of an
 * option that isn't there.
 */
export async function currentSelection(): Promise<PackSelection> {
  const available = await loadPackIndex();
  const stored = await storedSelection();
  if (stored === ALL_PACKS && available.length > 1) return ALL_PACKS;
  return resolveSelection(stored, available)[0];
}
